import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Output,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { RecentChats } from '../../shared/components/recent-chats/recent-chats';
import { HeaderService } from '../../shared/services/headerService';
import { TooltipModule } from 'primeng/tooltip';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TabsModule } from 'primeng/tabs';
import { UserService } from '../../shared/services/user.service';
import { environment } from '../../../environments/environment';
import { AgentChatStateService } from '../../features/agentic-ai/agent-chat-state.service';
import { AgenticAiApiService } from '../../features/agentic-ai/agentic-ai-api.service';
import type { AgenticAiConversationSearchHit } from '../../features/agentic-ai/agentic-ai.models';
import { formatConversationListDate } from '../../shared/utils/conversation-list-date.util';
import { NetworkConnectivityService } from '../../shared/services/network-connectivity.service';
import { TokensAllocationDrawer } from '../../features/agentic-ai/components/tokens-allocation-drawer/tokens-allocation-drawer';
import { UsageSummaryDrawer } from '../../features/agentic-ai/components/usage-summary-drawer/usage-summary-drawer';
import { AiGallery } from '../../views/ai-agent/components/ai-gallery/ai-gallery';
import { SkillsGallery } from '../../views/ai-agent/components/skills-gallery/skills-gallery';
import { showWorkflowBuilderUi } from '../../shared/utils/ats-unauthorized-redirect';

@Component({
  selector: 'app-ai-leftmenu',
  imports: [RecentChats, MenuModule, TooltipModule, DialogModule, DrawerModule, FormsModule, InputTextModule, TabsModule, TokensAllocationDrawer, UsageSummaryDrawer, AiGallery, SkillsGallery],
  templateUrl: './ai-leftmenu.html',
  styleUrl: './ai-leftmenu.scss'
})
export class AiLeftmenu {
  /** localStorage key holding the persisted color-scheme preference ('dark' | 'light'). */
  private static readonly THEME_STORAGE_KEY = 'ai-agent-theme';

  moreitems: MenuItem[] | undefined;
  /** Controls the Usage Report side drawer (opened from the user menu). */
  readonly usageReportVisible = signal(false);
  /** Controls the Tokens Allocation side drawer (opened from the user menu). */
  readonly tokensAllocationVisible = signal(false);
  /** Controls the AI Gallery dialog (opened from the user menu). */
  readonly aiGalleryVisible = signal(false);
  /** Controls the Skills Gallery dialog (opened from the user menu). */
  readonly skillsGalleryVisible = signal(false);
  setPopupVisible = false;
  collapsed = false;
  /** Whether the dark color scheme is active (persisted in localStorage, applied via `.app-dark` on <html>). */
  isDarkTheme = false;
  /** OS color-scheme query — followed when the user hasn't made an explicit choice. */
  private systemThemeQuery: MediaQueryList | null = null;
  tablesearch = '';
  searchLoading = false;
  searchError: string | null = null;
  searchResults: AgenticAiConversationSearchHit[] = [];
  searchResultTab = 'chats';
  chatMenuExpanded = true;
  workflowMenuExpanded = true;
  /** Generate Workflow / Recent Workflows — hidden in production. */
  readonly showWorkflowBuilder = showWorkflowBuilderUi();
  /** True after user submits search (Enter or button) — drives results vs hint text. */
  searchSubmitted = false;
  @Output() collapsedChange = new EventEmitter<boolean>();

  readonly user = inject(UserService);
  readonly network = inject(NetworkConnectivityService);
  private readonly router = inject(Router);
  private readonly agentChatState = inject(AgentChatStateService);
  private readonly agentApi = inject(AgenticAiApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly headerService = inject(HeaderService);
  private readonly destroyRef = inject(DestroyRef);

  toggleSidebar(): void {
    this.collapsed = !this.collapsed;
    this.collapsedChange.emit(this.collapsed);
  }

  toggleChatMenu(): void {
    this.chatMenuExpanded = !this.chatMenuExpanded;
  }

  toggleWorkflowMenu(): void {
    this.workflowMenuExpanded = !this.workflowMenuExpanded;
  }

  ngOnInit(): void {
    this.headerService
      .getAiSidebarVisibility()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((visible: boolean) => {
        this.collapsed = visible;
        this.collapsedChange.emit(this.collapsed);
      });
    this.applyStoredTheme();
    this.buildMoreItems();
  }

  /**
   * Applies the persisted theme preference, or follows the OS color scheme when the user
   * has not made an explicit choice. Sets the `.app-dark` class on the document root.
   */
  private applyStoredTheme(): void {
    const stored = localStorage.getItem(AiLeftmenu.THEME_STORAGE_KEY);
    this.isDarkTheme = stored ? stored === 'dark' : this.systemPrefersDark();
    document.documentElement.classList.toggle('app-dark', this.isDarkTheme);
    this.watchSystemTheme();
  }

  private systemPrefersDark(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  }

  /** While no explicit choice is stored, mirror live OS color-scheme changes. */
  private watchSystemTheme(): void {
    if (this.systemThemeQuery || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    this.systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemThemeQuery.addEventListener('change', (e) => {
      if (localStorage.getItem(AiLeftmenu.THEME_STORAGE_KEY)) {
        return;
      }
      this.isDarkTheme = e.matches;
      document.documentElement.classList.toggle('app-dark', this.isDarkTheme);
      this.buildMoreItems();
      this.cdr.markForCheck();
    });
  }

  private buildMoreItems(): void {
    const items: MenuItem[] = [
      {
        label: 'Agents Gallery',
        action: 'ai-gallery',
        icon: 'sparkles',
      },
      {
        label: 'Skills Gallery',
        action: 'skills-gallery',
        icon: 'lightbulb',
      },
      {
        label: 'Usage Summary',
        action: 'usage-report',
        icon: 'chart-pie',
      },
    ];
    if (this.user.isAdministrator()) {
      items.push({
        label: 'Tokens Allocation',
        action: 'tokens-allocation',
        icon: 'coins',
      });
    }
    items.push(
      {
        label: this.isDarkTheme ? 'Light Theme' : 'Dark Theme',
        action: 'toggle-theme',
        icon: this.isDarkTheme ? 'sun' : 'moon',
      },
      {
        separator: true,
      },
      { label: 'Log Out', action: 'logout', icon: 'log-out' },
    );
    this.moreitems = items;
  }

  /** Toggles the dark color scheme, persists the choice, and refreshes the menu label/icon. */
  private toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;
    document.documentElement.classList.toggle('app-dark', this.isDarkTheme);
    localStorage.setItem(AiLeftmenu.THEME_STORAGE_KEY, this.isDarkTheme ? 'dark' : 'light');
    this.buildMoreItems();
  }

  menuItemClick(action: string | undefined): void {
    if (action === 'ai-gallery') {
      this.aiGalleryVisible.set(true);
      return;
    }
    if (action === 'skills-gallery') {
      this.skillsGalleryVisible.set(true);
      return;
    }
    if (action === 'usage-report') {
      this.usageReportVisible.set(true);
      return;
    }
    if (action === 'tokens-allocation') {
      this.tokensAllocationVisible.set(true);
      return;
    }
    if (action === 'toggle-theme') {
      this.toggleTheme();
      return;
    }
    if (action === 'logout') {
      this.user.logoutRedirect(environment.defaultATSHost);
    }
  }

  /** Client-side navigation to a blank chat (no full page reload). */
  startNewChat(event: Event): void {
    event.preventDefault();
    this.agentChatState.requestPromptFocus();
    void this.router.navigate(['/ai-agent'], { replaceUrl: true });
  }

  /** Client-side navigation to the agent workflow builder (non-production only). */
  openWorkflowBuilder(event: Event): void {
    event.preventDefault();
    if (!this.showWorkflowBuilder) {
      return;
    }
    void this.router.navigate(['/workflow-builder']);
  }

  openSearchPopup(): void {
    this.setPopupVisible = true;
    this.clearSearchForm();
  }

  onSearchDialogVisibleChange(visible: boolean): void {
    if (!visible) {
      this.clearSearchForm();
    }
  }

  private clearSearchForm(): void {
    this.tablesearch = '';
    this.searchResults = [];
    this.searchError = null;
    this.searchLoading = false;
    this.searchSubmitted = false;
    this.searchResultTab = 'chats';
    this.cdr.markForCheck();
  }

  clearSearchInput(): void {
    this.tablesearch = '';
    this.searchResults = [];
    this.searchError = null;
    this.searchLoading = false;
    this.searchSubmitted = false;
    this.searchResultTab = 'chats';
    this.cdr.markForCheck();
  }

  onSearchTabChange(value: string | number | undefined): void {
    if (value === undefined || value === null || value === '') {
      return;
    }
    this.searchResultTab = String(value);
  }

  submitSearch(): void {
    if (!this.network.isOnline()) {
      return;
    }
    const q = String(this.tablesearch ?? '').trim();
    this.searchError = null;
    this.searchSubmitted = true;
    if (!q) {
      this.searchResults = [];
      this.searchLoading = false;
      this.cdr.markForCheck();
      return;
    }
    this.searchLoading = true;
    this.searchResults = [];
    this.cdr.markForCheck();
    this.agentApi.searchConversationsByTitle(q, { limit: 25 }).subscribe({
      next: (items) => {
        this.searchResults = items;
        this.searchLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.searchLoading = false;
        this.searchResults = [];
        this.searchError = 'Search failed. Please try again.';
        this.cdr.detectChanges();
      },
    });
  }

  openSearchResult(c: AgenticAiConversationSearchHit): void {
    this.clearSearchForm();
    this.setPopupVisible = false;
    void this.router.navigate(['/ai-agent', c.id], { replaceUrl: true });
  }

  searchResultTitle(c: AgenticAiConversationSearchHit): string {
    const t = c.title?.trim();
    return t ? t : 'Untitled chat';
  }

  isSearchTitleClipped(el: HTMLElement | null | undefined): boolean {
    if (!el) {
      return false;
    }
    return el.scrollWidth > el.clientWidth;
  }

  searchResultDateLabel(c: AgenticAiConversationSearchHit): string {
    return formatConversationListDate(c.updatedAt ?? c.createdAt);
  }
}
